import { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { tasksApi, projectsApi } from '../api/client';
import { useUIStore, useCalendarStore, useDataStore } from '../store';
import type { Task, Project } from '../types';
import {
  ChevronLeft,
  ChevronRight,
  Plus,
} from 'lucide-react';

// 获取月份的天数
function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

// 获取月份第一天是星期几
function getFirstDayOfMonth(year: number, month: number): number {
  return new Date(year, month, 1).getDay();
}

// 格式化日期
function formatDate(date: Date): string {
  return date.toISOString().split('T')[0];
}

// 格式化时间
function formatTime(dateStr: string | null): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' });
}

export function MonthView() {
  const { openTaskModal, setSelectedTaskId } = useUIStore();
  const { currentDate, setCurrentDate } = useCalendarStore();
  const { setTasks, tasks } = useDataStore();
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  // 获取任务列表
  const { data: tasksData, isLoading } = useQuery({
    queryKey: ['tasks'],
    queryFn: () => tasksApi.getAll(),
  });

  // 获取项目列表
  const { data: projectsData } = useQuery({
    queryKey: ['projects'],
    queryFn: () => projectsApi.getAll(),
  });

  useEffect(() => {
    if (tasksData?.success && tasksData.data) {
      setTasks(tasksData.data);
    }
  }, [tasksData, setTasks]);

  const projects = projectsData?.success ? projectsData.data : [];

  // 当前年月
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  // 月份数据
  const monthData = useMemo(() => {
    const daysInMonth = getDaysInMonth(year, month);
    const firstDay = getFirstDayOfMonth(year, month);
    const days: (Date | null)[] = [];

    // 填充月初空白
    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }

    // 填充日期
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }

    return days;
  }, [year, month]);

  // 获取某天的任务
  const getTasksForDay = (date: Date | null): Task[] => {
    if (!date) return [];
    const dateStr = formatDate(date);
    return tasks.filter((task) => {
      const taskDate = (task.startTime || task.dueDate || '').split('T')[0];
      return taskDate === dateStr;
    });
  };

  // 获取项目颜色
  const getProjectColor = (projectId: string) => {
    const project = projects.find((p) => p.id === projectId);
    return project?.color || '#6B7280';
  };

  // 获取状态颜色
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-700';
      case 'in_progress':
        return 'bg-blue-100 text-blue-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  // 导航到上个月
  const goToPrevious = () => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() - 1);
    setCurrentDate(newDate);
  };

  // 导航到下个月
  const goToNext = () => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + 1);
    setCurrentDate(newDate);
  };

  // 导航到今天
  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const handleTaskClick = (task: Task) => {
    setSelectedTaskId(task.id);
    openTaskModal(task.id);
  };

  const handleDateClick = (date: Date) => {
    setSelectedDate(date);
  };

  const handleAddTask = (date: Date) => {
    setSelectedDate(date);
    openTaskModal();
  };

  // 星期名称
  const dayNames = ['日', '一', '二', '三', '四', '五', '六'];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* 工具栏 */}
      <div className="flex items-center justify-between bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex items-center gap-4">
          <h2 className="text-xl font-semibold text-gray-900">
            {year}年{month + 1}月
          </h2>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={goToPrevious}
            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-all"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={goToToday}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-all border border-gray-300"
          >
            今天
          </button>
          <button
            onClick={goToNext}
            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-all"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* 月历 */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {/* 表头 */}
        <div className="grid grid-cols-7 border-b border-gray-200">
          {dayNames.map((name, index) => (
            <div
              key={index}
              className={`p-3 text-center text-sm font-medium ${
                index === 0 || index === 6 ? 'text-red-500' : 'text-gray-700'
              }`}
            >
              星期{name}
            </div>
          ))}
        </div>

        {/* 日期网格 */}
        <div className="grid grid-cols-7">
          {monthData.map((date, index) => {
            if (!date) {
              return (
                <div
                  key={index}
                  className="min-h-[120px] bg-gray-50 border-r border-b border-gray-200 last:border-r-0"
                />
              );
            }

            const dayTasks = getTasksForDay(date);
            const isToday = formatDate(date) === formatDate(new Date());
            const isSelected = selectedDate && formatDate(date) === formatDate(selectedDate);
            const isWeekend = date.getDay() === 0 || date.getDay() === 6;

            return (
              <div
                key={index}
                onClick={() => handleDateClick(date)}
                className={`min-h-[120px] p-2 border-r border-b border-gray-200 last:border-r-0 cursor-pointer transition-all hover:bg-gray-50 ${
                  isToday ? 'bg-blue-50' : ''
                } ${isSelected ? 'ring-2 ring-blue-500 ring-inset' : ''} ${
                  isWeekend ? 'bg-gray-50/50' : ''
                }`}
              >
                {/* 日期数字 */}
                <div className="flex items-center justify-between mb-1">
                  <span
                    className={`text-sm font-medium ${
                      isToday
                        ? 'w-7 h-7 flex items-center justify-center bg-blue-600 text-white rounded-full'
                        : isWeekend
                        ? 'text-red-500'
                        : 'text-gray-700'
                    }`}
                  >
                    {date.getDate()}
                  </span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAddTask(date);
                    }}
                    className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-all"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>

                {/* 任务列表 */}
                <div className="space-y-1">
                  {dayTasks.slice(0, 3).map((task) => (
                    <div
                      key={task.id}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleTaskClick(task);
                      }}
                      className={`px-2 py-1 rounded text-xs truncate cursor-pointer hover:opacity-80 transition-opacity ${getStatusColor(
                        task.status
                      )}`}
                      style={{
                        borderLeft: `3px solid ${getProjectColor(task.projectId)}`,
                      }}
                    >
                      {task.startTime && (
                        <span className="mr-1 opacity-75">
                          {formatTime(task.startTime)}
                        </span>
                      )}
                      {task.title}
                    </div>
                  ))}
                  {dayTasks.length > 3 && (
                    <div className="px-2 py-1 text-xs text-gray-500">
                      +{dayTasks.length - 3} 更多
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* 选中日期的任务详情 */}
      {selectedDate && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              {selectedDate.toLocaleDateString('zh-CN', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })} 的任务
            </h3>
            <button
              onClick={() => setSelectedDate(null)}
              className="text-gray-400 hover:text-gray-600"
            >
              ×
            </button>
          </div>

          <div className="space-y-2">
            {getTasksForDay(selectedDate).length === 0 ? (
              <p className="text-gray-500 text-center py-4">暂无任务</p>
            ) : (
              getTasksForDay(selectedDate).map((task) => (
                <div
                  key={task.id}
                  onClick={() => handleTaskClick(task)}
                  className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
                >
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: getProjectColor(task.projectId) }}
                  />
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{task.title}</p>
                    {task.startTime && (
                      <p className="text-sm text-gray-500">
                        {formatTime(task.startTime)} - {formatTime(task.endTime)}
                      </p>
                    )}
                  </div>
                  <span
                    className={`px-2 py-1 text-xs rounded-full ${getStatusColor(
                      task.status
                    )}`}
                  >
                    {task.status === 'completed'
                      ? '已完成'
                      : task.status === 'in_progress'
                      ? '进行中'
                      : '待办'}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
